import type { Lead } from '@/components/analyze/lead-capture-form';
import type { DCMAScoreResult } from '@/lib/dcma-scoring-engine';

export interface HubSpotSubmitOptions {
  portalId: string;
  formId: string;
  lead: Lead;
  scoreResult: DCMAScoreResult;
}

type HubSpotField = {
  name: string;
  value: string;
};

// Submit a captured lead to HubSpot via the browser-friendly Forms Submission
// API. This endpoint is CORS-enabled and requires no secret -- only the public
// portal ID and form GUID. The `dcma_grade` and `dcma_score` custom contact
// properties must already exist in the HubSpot portal.
export async function submitLeadToHubSpot({
  portalId,
  formId,
  lead,
  scoreResult,
}: HubSpotSubmitOptions): Promise<void> {
  const fields: HubSpotField[] = [
    { name: 'email', value: lead.email.trim() },
    { name: 'firstname', value: lead.firstName.trim() },
    { name: 'lastname', value: lead.lastName.trim() },
    { name: 'company', value: lead.company.trim() },
    { name: 'dcma_grade', value: scoreResult.grade },
    { name: 'dcma_score', value: String(scoreResult.totalScore) },
  ];

  const payload = {
    fields,
    context: {
      pageUri: typeof window !== 'undefined' ? window.location.href : undefined,
      pageName: typeof document !== 'undefined' ? document.title : undefined,
    },
  };

  const endpoint = `https://api.hsforms.com/submissions/v3/integration/submit/${encodeURIComponent(
    portalId,
  )}/${encodeURIComponent(formId)}`;

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`HubSpot submission failed with status ${response.status}`);
  }
}
